import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import ScoutApplicationModal from "@/components/common/ScoutApplicationModal";
import ContactTommyModal from "@/components/common/ContactTommyModal";
import {
  Target,
  Users,
  Trophy,
  Star,
  ArrowRight,
  CheckCircle,
  Calendar,
  MapPin,
  Award,
  TrendingUp,
  Eye,
  Clock,
  Globe,
  Zap,
  Shield,
  Heart,
  Video,
  UserPlus,
  FileText,
  Camera,
  Mic,
  ExternalLink,
  Search,
  GraduationCap,
  BookOpen,
  Navigation
} from "lucide-react";

export default function Scouts() {
  const [showScoutModal, setShowScoutModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-x-hidden">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-slate-900 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img 
            src="https://images.pexels.com/photos/6483427/pexels-photo-6483427.jpeg"
            alt="Scouts analyzing talent"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-secondary text-black px-6 py-3 text-sm font-black tracking-wider mb-8">
              PERSONALIZED GUIDANCE
            </Badge>
            <h1 className="text-5xl lg:text-7xl font-black mb-8 leading-tight">
              RECRUITLOOK
              <span className="block text-secondary">SCOUTS</span>
            </h1>
            <p className="text-xl lg:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Personalized guidance to student-athletes navigating the college recruiting process. 
              Our scouts provide comprehensive support from scouting to final commitment.
            </p>
          </div>

          {/* Service Pillars */}
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <Search className="w-12 h-12 text-secondary mx-auto mb-4" />
              <div className="text-2xl font-black text-secondary mb-2">SCOUT</div>
              <div className="text-sm font-semibold">Identify Top Talent</div>
            </div>
            <div className="text-center">
              <Target className="w-12 h-12 text-secondary mx-auto mb-4" />
              <div className="text-2xl font-black text-secondary mb-2">EVALUATE</div>
              <div className="text-sm font-semibold">Assess Potential</div>
            </div>
            <div className="text-center">
              <BookOpen className="w-12 h-12 text-secondary mx-auto mb-4" />
              <div className="text-2xl font-black text-secondary mb-2">EDUCATE</div>
              <div className="text-sm font-semibold">Process Knowledge</div>
            </div>
            <div className="text-center">
              <Navigation className="w-12 h-12 text-secondary mx-auto mb-4" />
              <div className="text-2xl font-black text-secondary mb-2">GUIDE</div>
              <div className="text-sm font-semibold">Personal Support</div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-black mb-6 text-slate-900">
              OUR
              <span className="block text-secondary">SERVICES</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive scouting services designed to maximize your recruiting potential
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Scouting */}
            <div className="bg-gradient-to-br from-slate-900 to-gray-900 text-white rounded-2xl p-8">
              <Search className="w-16 h-16 text-secondary mb-6" />
              <h3 className="text-3xl font-black mb-6">SCOUTING</h3>
              <div className="space-y-4 text-gray-300 mb-8">
                <p>
                  RecruitLook Scouts actively seek out top talent by attending practices and games 
                  to assess a recruit's potential and athletic capabilities.
                </p>
                <p>
                  Our scouts conduct in-home meetings to get a complete picture of each athlete's 
                  character, work ethic, and commitment to their sport and academics.
                </p>
              </div>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-secondary" />
                  <span>Live game and practice attendance</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-secondary" />
                  <span>In-home meetings and evaluations</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-secondary" />
                  <span>Character and work ethic assessment</span>
                </div>
              </div>
            </div>

            {/* Evaluation */}
            <div className="bg-white border-2 border-slate-200 rounded-2xl p-8">
              <Target className="w-16 h-16 text-secondary mb-6" />
              <h3 className="text-3xl font-black mb-6 text-slate-900">EVALUATION</h3>
              <div className="space-y-4 text-gray-600 mb-8">
                <p>
                  Continuous evaluation of athletes providing constructive feedback on athletic 
                  performance, academic standing, and recruiting strategies.
                </p>
                <p>
                  We ensure recruits are effectively managing their college recruitment process 
                  and making informed decisions about their future.
                </p>
              </div>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-secondary" />
                  <span>Athletic performance analysis</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-secondary" />
                  <span>Academic progress monitoring</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-secondary" />
                  <span>Strategic recruiting feedback</span>
                </div>
              </div>
            </div>

            {/* Education */}
            <div className="bg-secondary text-black rounded-2xl p-8">
              <BookOpen className="w-16 h-16 text-slate-900 mb-6" />
              <h3 className="text-3xl font-black mb-6">EDUCATION</h3>
              <div className="space-y-4 text-slate-800 mb-8">
                <p>
                  RecruitLook educates families and recruits on every aspect of the recruiting 
                  process, including marketing strategies and communication with college coaches.
                </p>
                <p>
                  Comprehensive education on NCAA rules, financial aid, and the nuances of 
                  official and unofficial visits to ensure informed decisions.
                </p>
              </div>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-slate-900" />
                  <span>Marketing strategies and tools</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-slate-900" />
                  <span>NCAA rules and compliance</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-slate-900" />
                  <span>Financial aid and scholarship guidance</span>
                </div>
              </div>
            </div>

            {/* Guidance */}
            <div className="bg-gradient-to-br from-gray-100 to-white border-2 border-secondary rounded-2xl p-8">
              <Navigation className="w-16 h-16 text-secondary mb-6" />
              <h3 className="text-3xl font-black mb-6 text-slate-900">GUIDANCE</h3>
              <div className="space-y-4 text-gray-600 mb-8">
                <p>
                  Each RecruitLook Scout offers personalized support, providing insights into 
                  the recruiting process and marketing tools to maximize exposure.
                </p>
                <p>
                  Live support for recruiting questions and comprehensive knowledge to transition 
                  from high school recruit to legitimate college prospect.
                </p>
              </div>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-secondary" />
                  <span>Personalized recruiting strategies</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-secondary" />
                  <span>Live support and consultation</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-secondary" />
                  <span>College prospect development</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Scout Process */}
      <section className="py-24 bg-gradient-to-br from-gray-100 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-black mb-6 text-slate-900">
              THE SCOUTING
              <span className="block text-secondary">PROCESS</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our comprehensive four-step approach to maximizing your recruiting potential
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Step 1 */}
            <div className="text-center">
              <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-black text-black">1</span>
              </div>
              <h3 className="text-2xl font-black mb-4 text-slate-900">DISCOVER</h3>
              <p className="text-gray-600">
                Our scouts attend games and practices to identify top talent and assess athletic potential in real-time competition.
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div className="w-20 h-20 bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-black text-secondary">2</span>
              </div>
              <h3 className="text-2xl font-black mb-4 text-slate-900">ASSESS</h3>
              <p className="text-gray-600">
                Comprehensive evaluation of athletic skills, academic standing, character, and recruiting readiness.
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center">
              <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-black text-black">3</span>
              </div>
              <h3 className="text-2xl font-black mb-4 text-slate-900">EDUCATE</h3>
              <p className="text-gray-600">
                Provide comprehensive education on the recruiting process, NCAA rules, and strategic marketing approaches.
              </p>
            </div>

            {/* Step 4 */}
            <div className="text-center">
              <div className="w-20 h-20 bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-black text-secondary">4</span>
              </div>
              <h3 className="text-2xl font-black mb-4 text-slate-900">DELIVER</h3>
              <p className="text-gray-600">
                Ongoing personalized guidance and support throughout the entire recruiting journey to college commitment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Meet Our Lead Scout */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-black mb-6 text-slate-900">
              MEET OUR
              <span className="block text-secondary">LEAD SCOUT</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get to know the experienced professional leading our scouting efforts
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-slate-900 to-gray-900 text-white rounded-2xl overflow-hidden">
              <div className="grid lg:grid-cols-2 gap-0">
                {/* Scout Image */}
                <div className="relative h-96 lg:h-auto overflow-hidden">
                  <img
                    src="/lovable-uploads/28cd6465-b196-49f5-af50-369f772382b1.png"
                    alt="Tommy Canale - Lead Scout"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  <div className="absolute bottom-6 left-6">
                    <Badge className="bg-secondary text-black px-3 py-1 text-xs font-black">
                      LEAD SCOUT
                    </Badge>
                  </div>
                </div>

                {/* Scout Information */}
                <div className="p-8 lg:p-12">
                  <h3 className="text-3xl lg:text-4xl font-black mb-4">TOMMY CANALE</h3>
                  <p className="text-secondary font-bold text-lg mb-6">National Scout & Scouting Director</p>

                  <div className="space-y-4 text-gray-300 mb-8">
                    <p>
                      Tommy brings extensive experience as a former Athletic Director and basketball coach
                      at Illinois Valley Community College, providing deep insight into both sides of
                      the recruiting process.
                    </p>
                    <p>
                      His background in athletics administration and coaching gives him unique perspective
                      in identifying talent and understanding what college programs are looking for in
                      prospective student-athletes.
                    </p>
                    <p>
                      Tommy leads our scouting efforts with a comprehensive approach that evaluates not
                      just athletic ability, but character, work ethic, and academic potential.
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <div>
                      <h4 className="text-lg font-black mb-3 text-secondary">EXPERIENCE</h4>
                      <div className="space-y-2 text-sm">
                        <div>• Former Athletic Director</div>
                        <div>• Basketball Coach</div>
                        <div>• Illinois Valley Community College</div>
                        <div>• Scouting Director at RecruitLook</div>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-lg font-black mb-3 text-secondary">EXPERTISE</h4>
                      <div className="space-y-2 text-sm">
                        <div>• Talent Identification</div>
                        <div>• Athletic Administration</div>
                        <div>• Recruiting Process</div>
                        <div>• Player Development</div>
                      </div>
                    </div>
                  </div>

                  <Button 
                    className="bg-secondary text-black hover:bg-secondary/90 font-black"
                    onClick={() => setShowContactModal(true)}
                  >
                    CONTACT TOMMY
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Scout's Approach */}
            <div className="mt-12 grid md:grid-cols-3 gap-8">
              <div className="text-center p-6 bg-gradient-to-br from-gray-100 to-white rounded-xl border border-gray-200">
                <Eye className="w-12 h-12 text-secondary mx-auto mb-4" />
                <h4 className="text-xl font-black mb-3 text-slate-900">COMPREHENSIVE EVALUATION</h4>
                <p className="text-gray-600 text-sm">
                  Assesses athletic ability, character, academics, and potential for college-level success.
                </p>
              </div>
              <div className="text-center p-6 bg-gradient-to-br from-gray-100 to-white rounded-xl border border-gray-200">
                <Users className="w-12 h-12 text-secondary mx-auto mb-4" />
                <h4 className="text-xl font-black mb-3 text-slate-900">PERSONALIZED GUIDANCE</h4>
                <p className="text-gray-600 text-sm">
                  Provides individualized support tailored to each athlete's unique goals and circumstances.
                </p>
              </div>
              <div className="text-center p-6 bg-gradient-to-br from-gray-100 to-white rounded-xl border border-gray-200">
                <Shield className="w-12 h-12 text-secondary mx-auto mb-4" />
                <h4 className="text-xl font-black mb-3 text-slate-900">PROVEN TRACK RECORD</h4>
                <p className="text-gray-600 text-sm">
                  Years of experience helping student-athletes navigate the recruiting process successfully.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Become a Scout */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="bg-secondary text-black px-4 py-2 font-black mb-6">
                JOIN OUR TEAM
              </Badge>
              <h2 className="text-4xl lg:text-5xl font-black mb-8">
                BECOME A
                <span className="block text-secondary">RECRUITLOOK SCOUT</span>
              </h2>
              <div className="space-y-6 text-lg text-gray-300 leading-relaxed">
                <p>
                  Passionate about helping high school athletes achieve their college dreams? 
                  Join our team of dedicated scouts who make a real difference in young athletes' lives.
                </p>
                <p>
                  Ideal candidates have access to high school athletes, are willing to attend sporting 
                  events to scout new talent, and possess the dedication and organizational skills 
                  necessary to evaluate talent effectively.
                </p>
              </div>
              
              <div className="space-y-4 my-8">
                <div className="flex items-center space-x-4">
                  <CheckCircle className="w-6 h-6 text-secondary" />
                  <span className="text-lg">Passion for helping student-athletes</span>
                </div>
                <div className="flex items-center space-x-4">
                  <CheckCircle className="w-6 h-6 text-secondary" />
                  <span className="text-lg">Access to high school athletic programs</span>
                </div>
                <div className="flex items-center space-x-4">
                  <CheckCircle className="w-6 h-6 text-secondary" />
                  <span className="text-lg">Strong organizational and evaluation skills</span>
                </div>
                <div className="flex items-center space-x-4">
                  <CheckCircle className="w-6 h-6 text-secondary" />
                  <span className="text-lg">Willingness to attend sporting events</span>
                </div>
              </div>

              <Button 
                onClick={() => setShowScoutModal(true)}
                className="bg-secondary text-black hover:bg-secondary/90 font-black px-8 py-4"
              >
                APPLY TO BECOME A SCOUT
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/6483427/pexels-photo-6483427.jpeg"
                alt="Scout at game"
                className="w-full h-96 object-cover rounded-2xl shadow-lg"
              />
              <div className="absolute -bottom-6 -right-6 bg-secondary text-black p-6 rounded-xl">
                <div className="text-2xl font-black">JOIN</div>
                <div className="text-sm font-bold">OUR TEAM</div>
              </div>
            </div>
          </div>
        </div>
      </section>



      <ScoutApplicationModal 
        open={showScoutModal} 
        onOpenChange={setShowScoutModal} 
      />
      
      <ContactTommyModal 
        open={showContactModal} 
        onOpenChange={setShowContactModal} 
      />
    </div>
  );
}
